import { ImportEntity } from '@paintscout/api'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { EstimatorSelect } from '@paintscout/ui-business'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util'
import React from 'react'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  help: {
    color: theme.palette.text.secondary
  }
}))

/** The same roles the Owner select on a contact or company offers (EditContact, EditCompany). */
export const IMPORT_OWNER_ROLES = ['admin', 'sales', 'viewer', 'core', 'team']

export interface ImportOwnerSelectProps {
  entity: ImportEntity
  entityNounPlural: string
  /** Support and superadmin must choose; a company admin's owner defaults to themselves. */
  required: boolean
  /** Without the `auth0|` prefix. */
  ownerId?: string
  onChange: (ownerId?: string) => void
}

/**
 * Review step: the account user who will own the new records. Required for support and superadmin
 * (without it the support user owned every imported contact); optional for company admins, shown
 * with themselves selected. Other users never see it (`getImportOwnerChoice`).
 */
export default function ImportOwnerSelect({
  entity,
  entityNounPlural,
  required,
  ownerId,
  onChange
}: ImportOwnerSelectProps) {
  const classes = useStyles()
  const owned =
    entity === ImportEntity.Contact
      ? `New ${entityNounPlural} are owned by and assigned to this user.`
      : `New ${entityNounPlural} are owned by this user.`
  const help = `${owned} Existing ${entityNounPlural} keep their owner.${required ? '' : ' Defaults to you.'}`

  return (
    <div className={classes.root} data-testid="review-owner">
      <Typography variant="h4">Owner</Typography>
      <Typography variant="body2" className={classes.help}>
        {help}
      </Typography>
      <EstimatorSelect
        name="importOwner"
        label=""
        fullWidth
        variant="single"
        searchable
        roles={IMPORT_OWNER_ROLES}
        includeUser={(user) => user.app_metadata?.active !== false}
        value={ownerId ? addAuth0Prefix(ownerId) : null}
        placeholder={{ selectPlaceholder: 'Choose an owner', plural: 'users' }}
        userFields={['user_metadata.playerName', 'picture', 'user_metadata.customAvatar.value']}
        onChange={(option: DashboardEstimator | null) =>
          onChange(option?.value ? removeAuth0Prefix(option.value) : undefined)
        }
        autoApply
      />
    </div>
  )
}
